/**
 * Return the next or previous page cyclically,
 *    If activePage is the last page and pageDelta is 1 it return 0
 *    If activePage is 0 and pageDelta is -1 it return (totalPages -1)
 * @param {Number} pageDelta, Pagination direction
 * @param {Number} activePage, Current active page
 * @param {Number} totalPages, Total number of pages
 */
const calculateCyclePage = (pageDelta, activePage, totalPages) =>
  (activePage + pageDelta + totalPages) % totalPages;

/**
 * Return the corresponded translation of the given page.
 * This way the previous page is translateX -100%,
 * active page is translateX 0%, next page is traslateX 100%
 * and the others are moved out of the main views ( translateX 200% )
 * @param {Number} page, Index of the page in the array of pages
 * @param {Number} activePage, Current active page
 * @param {Number} totalPages, Total number of pages
 */
const pageTranslation = (page, activePage, totalPages) => {
  switch (page) {
    // Current item centered on screen
    case activePage:
      return `translateX(0%)`;
    // Previous item on the left
    case calculateCyclePage(-1, activePage, totalPages):
      return `translateX(-100%)`;
    // Next item on the right
    case calculateCyclePage(1, activePage, totalPages):
      return `translateX(100%)`;
    // Other items are moved out of the main view
    default:
      return `translateX(${page < activePage ? -100 : 100}%)`;
  }
};

/**
 * Return the corresponded translation of the given page.
 * This way the previous page is translateX -100%,
 * active page is translateX 0%, next page is traslateX 100%
 * and the others are moved out of the main views ( translateX 200% )
 * @param {Number} page, Index of the page in the array of pages
 * @param {Number} activePage, Current active page
 * @param {Number} totalPages, Total number of pages
 * @param {Number} offsetPercentage, Percentage of offset (For animate gestures)
 */
const pageTranslationWithOffset = (
  page,
  activePage,
  totalPages,
  offsetPercentage = 0
) => {
  switch (page) {
    // Current item centered on screen
    case activePage:
      return `translateX(${offsetPercentage}%)`;
    // Previous item on the left
    case calculateCyclePage(-1, activePage, totalPages):
      return `translateX(${offsetPercentage - 100}%)`;
    // Next item on the right
    case calculateCyclePage(1, activePage, totalPages):
      return `translateX(${offsetPercentage + 100}%)`;
    // Other items are moved out of the main view
    default:
      return `translateX(${page < activePage ? -100 : 100}%)`;
  }
};

/**
 * Return the animation to take out the previous active page
 * and move the current active page to main position
 * @param {Number} page, Index of the page in the array of pages
 * @param {Number} activePage, Page that should go to main position
 * @param {Number} previousActivePage, Page that should take out of main position
 */
const pageTransition = (page, activePage, previousActivePage) =>
  page === activePage || page === previousActivePage
    ? 'all 500ms ease-in-out'
    : '';

/**
 * Return if the slide gesture is enough to replace current main carousel item for other.
 *    It return true if offset is greater than half the width (50%) or
 *    if slide duration is less than 250ms and offset is greater than 20px
 * @param {Number} offsetPercentage
 * @param {Number} startGestureX
 * @param {Number} currentGestureX
 * @param {Number} startGestureTime
 */
const isValidSlide = (
  offsetPercentage,
  startGestureX,
  currentGestureX,
  startGestureTime
) =>
  Math.abs(offsetPercentage) >= 50 ||
  (new Date().getTime() - startGestureTime < 250 &&
    Math.abs(currentGestureX - startGestureX) >= 20);

export {
  calculateCyclePage,
  pageTranslation,
  pageTranslationWithOffset,
  pageTransition,
  isValidSlide
};
